import React from "react";
import { Loader2 } from "lucide-react";

/**
 * Premium button — own visual system, not Apple-derivative.
 * Sharp corners consistent with brand, generous padding, precise focus ring.
 *
 * Variants:
 *  - primary:  filled brand, white text — for main CTAs
 *  - secondary: outlined, ink text — for confirming secondary actions
 *  - ghost:    text-only, muted → ink on hover — for tertiary
 *  - danger:   text-only red for destructive, becomes filled on hover
 *
 * States handled: hover, active (press-scale), focus (2px outline offset),
 * loading (spinner + disabled), disabled (opacity + no interaction).
 *
 * Sizes: sm (h-9), md (h-11 default), lg (h-12).
 */
export const Button = React.forwardRef(function Button(
  { variant = "primary", size = "md", loading = false, disabled = false,
    icon: Icon, iconRight: IconRight, children, className = "", type = "button",
    ...rest },
  ref
) {
  const isDisabled = disabled || loading;

  const base =
    "relative inline-flex items-center justify-center gap-2 select-none " +
    "font-medium tracking-tight rounded-[10px] " +
    "transition-all duration-150 ease-out " +
    "active:scale-[0.98] " +
    "focus-visible:outline-none focus-visible:ring-0 " +
    "focus-visible:[box-shadow:0_0_0_2px_white,0_0_0_4px_var(--focus-ring)]";

  const sizeMap = {
    sm: "h-9 px-4 text-[13px]",
    md: "h-11 px-5 text-[14px]",
    lg: "h-12 px-6 text-[15px]",
  };

  const variantMap = {
    primary:
      "bg-ink text-white " +
      "hover:bg-black " +
      "disabled:bg-neutral-200 disabled:text-neutral-400",
    secondary:
      "bg-white text-ink border border-neutral-300 " +
      "hover:border-ink hover:bg-neutral-50 " +
      "disabled:border-neutral-200 disabled:text-neutral-400",
    ghost:
      "bg-transparent text-ink-muted " +
      "hover:text-ink hover:bg-neutral-100 " +
      "disabled:text-neutral-300",
    danger:
      "bg-transparent text-red-600 " +
      "hover:bg-red-50 " +
      "disabled:text-neutral-300",
    "danger-solid":
      "bg-red-600 text-white " +
      "hover:bg-red-700 " +
      "disabled:bg-neutral-200 disabled:text-neutral-400",
    brand:
      "bg-brand text-white " +
      "hover:bg-brand-hover " +
      "disabled:bg-neutral-200 disabled:text-neutral-400",
  };

  const cls = [
    base,
    sizeMap[size],
    variantMap[variant],
    isDisabled ? "cursor-not-allowed" : "cursor-pointer",
    className,
  ].join(" ");

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={cls}
      style={{ "--focus-ring": "rgba(61, 104, 177, 0.6)" }}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : Icon ? (
        <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
      ) : null}
      <span>{children}</span>
      {IconRight && !loading && <IconRight className="h-4 w-4 shrink-0" strokeWidth={2} />}
    </button>
  );
});

export default Button;
